<script lang="ts" setup>
import { ref, watch, computed } from 'vue';
import { useQuasar } from 'quasar';  // Import Quasar utilities
// import type { SearchResult } from '@nuxt/content'
import { type SearchResult as MiniSearchResult } from 'minisearch'; 

const search = ref('');
const showSearchDialog = ref(false); // Control the visibility of the dialog

// Extend the existing SearchResult type from MiniSearch
interface ExtendedSearchResult extends MiniSearchResult {
  content: string;           // Adding content field
  titles: string[];          // Adding titles field
  match: {                   // Modifying match to include the matched fields
    [key: string]: string[];
  };
}

const results = ref<ExtendedSearchResult[]>([]);

// Watch for changes in the search query and fetch results
watch(search, async (newQuery) => {
  if (newQuery.length > 2) { // Trigger search after 3 characters
    showSearchDialog.value = true;  // Show the dialog when we have search results
  } else {
    results.value = [];
    showSearchDialog.value = false; // Hide the dialog if search query is too short
  }
});

// Computed property to check if results exist
const hasResults = computed(() => results.value.length > 0);

// Function to get a snippet of the content (limit to 50 characters)
function getSnippet(content: string): string {
  const snippetLength = 50;
  return content.length > snippetLength ? content.slice(0, snippetLength) + '...' : content;
}

</script>

<template>
  <main>
    <!-- Search Input -->
    <q-input dark outlined v-model="search" label="Search" :dense="true">
        <template v-slot:append>
          <q-icon v-if="search !== ''" name="close" @click="search = ''" class="cursor-pointer" />
          <q-icon name="search" />
        </template>
    </q-input>

    <!-- Search Results Dialog -->
    <SearchDialog
      :showDialog="showSearchDialog"
      :initialSearch="search"
      :initialResults="results"
      @close="showSearchDialog = false"
    />
  </main>
</template>

<style scoped>
q-input {
  width: 100%;
  max-width: 500px;

  input {
    background-color: white; /* Set background color to white */
    color: black; /* Ensure the text inside the input is readable */
  }

  .q-field__control {
    border: 1px solid white; /* Optional: Set a white border */
  }
}

/* Snippet styling */
.content-snippet {
  color: #666;
  font-size: 0.9rem;
  margin-top: 0.5rem;
}

</style>